import React, { useEffect, useState } from 'react';
import '../../style/App.scss';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Snackbar,
  styled,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import useSWR from 'swr';
import useNavigationStore from '../../store/zustand/navigation';
import Api from '../../api';
import {
  mainColors,
  secondaryColors,
  alertColors,
  successColors,
  accentColors
} from '../../theme/themeConfig';

const MainContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  padding: theme.spacing(4),
  width: '100%'
}));

const CardContainer = styled(Box)(() => ({
  background: '#fff',
  border: `1px solid ${secondaryColors.SECONDARY_6}`,
  borderRadius: '12px',
  boxShadow: '0 1px 3px rgba(15,28,46,.06)',
  overflow: 'hidden'
}));

const STATUS_COLORS: Record<string, string> = {
  authorized: mainColors.MAIN_6,
  cancelled: secondaryColors.SECONDARY,
  completed: successColors.SUCCESS,
  dispatched: mainColors.MAIN,
  in_progress: accentColors.ACCENT,
  invoiced: successColors.SUCCESS_5,
  pending_authorization: '#8B5CF6',
  pending_review: '#F59E0B',
  rejected: alertColors.ALERT
};

function formatDate(iso?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
}

function formatDateTime(iso?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    month: 'short'
  });
}

const ServiceRequestsClient: React.FC = function ServiceRequestsClient() {
  const { setCurrentStepScreen } = useNavigationStore((state) => state);
  const [selectedSR, setSelectedSR] = useState<any>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [snackMsg, setSnackMsg] = useState('');

  useEffect(() => {
    setCurrentStepScreen('client_service_requests');
  }, []);

  const {
    data: srList = [],
    error,
    mutate
  } = useSWR('portal-service-requests', () =>
    Api.ClientApi.getPortalServiceRequests()
  );

  const isLoading = srList.length === 0 && !error;

  const handleAuthorize = async (action: 'approve' | 'decline') => {
    if (!selectedSR) return;
    setActionLoading(true);
    try {
      await Api.ClientApi.authorizePortalServiceRequest(selectedSR.id, action);
      setSnackMsg(
        action === 'approve'
          ? 'Service request approved'
          : 'Service request declined'
      );
      setSelectedSR(null);
      await mutate();
    } catch (e: any) {
      setSnackMsg(e?.response?.data?.message || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const pendingAuth = (srList as any[]).filter(
    (sr) => sr.status === 'pending_authorization'
  );
  const others = (srList as any[]).filter(
    (sr) => sr.status !== 'pending_authorization'
  );

  if (isLoading) {
    return (
      <MainContainer>
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: '60px' }}>
          <CircularProgress />
        </Box>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <Box>
        <Typography sx={{ fontSize: '22px', fontWeight: 700 }}>
          Service Requests
        </Typography>
        <Typography sx={{ color: secondaryColors.SECONDARY, fontSize: '13px' }}>
          Ad-hoc service requests from your service provider
        </Typography>
      </Box>

      {/* Pending authorization — prominent section */}
      {pendingAuth.length > 0 && (
        <CardContainer>
          <Box
            sx={{
              background: '#FDF4FF',
              borderBottom: `1px solid #E9D5FF`,
              p: '14px 20px'
            }}
          >
            <Typography
              sx={{ color: '#7C3AED', fontSize: '13px', fontWeight: 700 }}
            >
              ⚡ Action Required — {pendingAuth.length} request
              {pendingAuth.length > 1 ? 's' : ''} awaiting your authorization
            </Typography>
          </Box>
          {pendingAuth.map((sr: any) => (
            <Box
              key={sr.id}
              sx={{
                '&:last-child': { borderBottom: 'none' },
                borderBottom: `1px solid ${secondaryColors.SECONDARY_6}`,
                p: '16px 20px'
              }}
            >
              <Box
                sx={{
                  alignItems: 'flex-start',
                  display: 'flex',
                  justifyContent: 'space-between',
                  mb: '8px'
                }}
              >
                <Box>
                  <Typography sx={{ fontSize: '15px', fontWeight: 700 }}>
                    {sr.title}
                  </Typography>
                  <Typography
                    sx={{
                      color: secondaryColors.SECONDARY,
                      fontSize: '12px',
                      mt: '2px'
                    }}
                  >
                    Requested: {formatDate(sr.requestedDate)} ·{' '}
                    {sr.estimatedDurationMinutes
                      ? `${sr.estimatedDurationMinutes} min`
                      : ''}
                  </Typography>
                </Box>
                {Number(sr.totalAmount) > 0 && (
                  <Typography
                    sx={{
                      color: mainColors.MAIN,
                      fontSize: '18px',
                      fontWeight: 700
                    }}
                  >
                    ${Number(sr.totalAmount).toFixed(2)}
                  </Typography>
                )}
              </Box>

              {sr.description && (
                <Typography
                  sx={{ color: '#374151', fontSize: '13px', mb: '12px' }}
                >
                  {sr.description}
                </Typography>
              )}

              {/* Line items */}
              {(sr.lineItems ?? []).length > 0 && (
                <Box
                  sx={{
                    background: '#F8FAFC',
                    borderRadius: '8px',
                    mb: '14px',
                    overflow: 'hidden'
                  }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{
                            color: secondaryColors.SECONDARY,
                            fontSize: '11px',
                            fontWeight: 600,
                            textTransform: 'uppercase'
                          }}
                        >
                          Service
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            color: secondaryColors.SECONDARY,
                            fontSize: '11px',
                            fontWeight: 600
                          }}
                        >
                          Qty
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            color: secondaryColors.SECONDARY,
                            fontSize: '11px',
                            fontWeight: 600
                          }}
                        >
                          Unit
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            color: secondaryColors.SECONDARY,
                            fontSize: '11px',
                            fontWeight: 600
                          }}
                        >
                          Total
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(sr.lineItems ?? []).map((item: any) => (
                        <TableRow key={item.id}>
                          <TableCell sx={{ fontSize: '12px' }}>
                            {item.description}
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: '12px' }}>
                            {item.quantity}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ color: '#94A3B8', fontSize: '12px' }}
                          >
                            {item.unit || '—'}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontSize: '12px', fontWeight: 600 }}
                          >
                            ${Number(item.total).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: '10px' }}>
                <Button
                  onClick={() => setSelectedSR(sr)}
                  size="small"
                  startIcon={<CheckCircleOutlineIcon />}
                  sx={{
                    '&:hover': { background: mainColors.MAIN_3 },
                    background: mainColors.MAIN,
                    color: '#fff',
                    fontSize: '12px'
                  }}
                  variant="contained"
                >
                  Approve
                </Button>
                <Button
                  onClick={async () => {
                    setSelectedSR(sr);
                    // Immediate decline without confirmation dialog
                  }}
                  size="small"
                  startIcon={<CancelOutlinedIcon />}
                  sx={{ color: alertColors.ALERT, fontSize: '12px' }}
                  variant="outlined"
                >
                  Decline
                </Button>
              </Box>
            </Box>
          ))}
        </CardContainer>
      )}

      {/* All other SRs */}
      {others.length > 0 && (
        <CardContainer>
          <Box
            sx={{
              background: '#F4F7FB',
              borderBottom: `1px solid ${secondaryColors.SECONDARY_6}`,
              p: '12px 20px'
            }}
          >
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 700,
                letterSpacing: '.4px',
                textTransform: 'uppercase'
              }}
            >
              History
            </Typography>
          </Box>
          {others.map((sr: any, idx: number) => {
            const statusColor =
              STATUS_COLORS[sr.status] ?? secondaryColors.SECONDARY;
            return (
              <Box
                key={sr.id}
                sx={{
                  alignItems: 'center',
                  borderBottom:
                    idx < others.length - 1
                      ? `1px solid ${secondaryColors.SECONDARY_6}`
                      : 'none',
                  display: 'flex',
                  justifyContent: 'space-between',
                  p: '14px 20px'
                }}
              >
                <Box>
                  <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
                    {sr.title}
                  </Typography>
                  <Typography
                    sx={{
                      color: secondaryColors.SECONDARY,
                      fontSize: '11px',
                      mt: '2px'
                    }}
                  >
                    {formatDateTime(sr.createdAt)}
                    {sr.authorizedAt &&
                      ` · Authorized ${formatDate(sr.authorizedAt)}`}
                  </Typography>
                </Box>
                <Box
                  sx={{ alignItems: 'center', display: 'flex', gap: '12px' }}
                >
                  {Number(sr.totalAmount) > 0 && (
                    <Typography
                      sx={{
                        color: mainColors.MAIN,
                        fontSize: '13px',
                        fontWeight: 700
                      }}
                    >
                      ${Number(sr.totalAmount).toFixed(2)}
                    </Typography>
                  )}
                  <Chip
                    label={sr.status.replace(/_/g, ' ')}
                    size="small"
                    sx={{
                      background: `${statusColor}18`,
                      color: statusColor,
                      fontSize: '10px',
                      fontWeight: 700,
                      height: '20px',
                      textTransform: 'capitalize'
                    }}
                  />
                </Box>
              </Box>
            );
          })}
        </CardContainer>
      )}

      {srList.length === 0 && !isLoading && (
        <Box
          sx={{
            border: `1px dashed ${secondaryColors.SECONDARY_6}`,
            borderRadius: '12px',
            color: '#CBD5E1',
            fontSize: '13px',
            p: '48px',
            textAlign: 'center'
          }}
        >
          No service requests yet
        </Box>
      )}

      {/* Confirmation dialog */}
      <Dialog
        maxWidth="xs"
        onClose={() => setSelectedSR(null)}
        open={Boolean(selectedSR)}
      >
        <DialogTitle sx={{ fontSize: '16px', fontWeight: 700 }}>
          Authorize Service Request
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '13px' }}>
            <strong>{selectedSR?.title}</strong>
          </Typography>
          {Number(selectedSR?.totalAmount) > 0 && (
            <Typography
              sx={{
                color: mainColors.MAIN,
                fontSize: '18px',
                fontWeight: 700,
                mt: '8px'
              }}
            >
              ${Number(selectedSR?.totalAmount).toFixed(2)}
            </Typography>
          )}
          <Divider sx={{ my: '12px' }} />
          <Typography sx={{ color: '#64748B', fontSize: '12px' }}>
            By approving, you authorize your service provider to perform this
            work and bill accordingly.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: '12px 24px' }}>
          <Button
            disabled={actionLoading}
            onClick={() => handleAuthorize('decline')}
            sx={{ color: alertColors.ALERT }}
            variant="outlined"
          >
            Decline
          </Button>
          <Button
            disabled={actionLoading}
            onClick={() => handleAuthorize('approve')}
            sx={{
              '&:hover': { background: mainColors.MAIN_3 },
              background: mainColors.MAIN,
              color: '#fff'
            }}
            variant="contained"
          >
            {actionLoading ? (
              <CircularProgress size={18} sx={{ color: '#fff' }} />
            ) : (
              'Approve'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        autoHideDuration={3000}
        message={snackMsg}
        onClose={() => setSnackMsg('')}
        open={Boolean(snackMsg)}
      />
    </MainContainer>
  );
};

export default ServiceRequestsClient;
